import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  isPlayBillingAvailable,
  getPlanDetails,
  purchasePlan,
  restorePurchases,
} from "@/lib/googlePlayBilling";

// Android (TWA) upgrade panel. Uses Google Play Billing for the in-app purchase,
// as required by Play policy. The website keeps Paystack; this component is only
// rendered inside the Debii Android app.
export const GooglePlayPlan = ({ productId, onEntitled }) => {
  const { refreshUser } = useAuth();
  const available = isPlayBillingAvailable();
  const [details, setDetails] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!available || !productId) return;
    getPlanDetails(productId)
      .then(setDetails)
      .catch(() => setDetails(null));
  }, [available, productId]);

  const finish = async () => {
    await refreshUser();
    onEntitled && onEntitled();
  };

  const buy = async () => {
    setBusy(true);
    try {
      await purchasePlan(productId);
      toast.success("Debii Plus is now active. Thank you!");
      await finish();
    } catch (e) {
      const m = e?.message || "";
      if (m !== "AbortError" && !/abort|cancel/i.test(m)) {
        toast.error("We couldn't complete the purchase. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    setBusy(true);
    try {
      const res = await restorePurchases(productId);
      const entitled = res?.entitlement?.plan === "plus";
      toast[entitled ? "success" : "message"](
        entitled ? "Your Debii Plus has been restored." : "No active Google Play subscription found."
      );
      await finish();
    } catch {
      toast.error("Couldn't restore purchases. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!available) {
    return (
      <div data-testid="play-billing-unavailable-banner" className="rounded-2xl bg-secondary border border-border p-4 text-center">
        <p className="font-semibold text-foreground">Install Debii from Google Play to upgrade</p>
        <p className="mt-1 text-xs text-muted-foreground">
          In-app upgrade uses Google Play Billing, available when you open Debii from the app installed
          via Google Play. If you already subscribed on the web, your Plus is active here automatically.
        </p>
        <Button
          data-testid="play-restore-button"
          variant="outline"
          onClick={restore}
          disabled={busy}
          className="mt-3 w-full h-11 rounded-xl"
        >
          Restore purchase
        </Button>
      </div>
    );
  }

  const priceLabel = details?.price
    ? new Intl.NumberFormat(undefined, { style: "currency", currency: details.price.currency })
        .format(Number(details.price.value))
    : null;

  return (
    <div className="space-y-3">
      <Button
        data-testid="play-subscribe-button"
        onClick={buy}
        disabled={busy}
        className="w-full h-14 rounded-2xl text-base"
      >
        {busy ? "Opening Google Play…" : priceLabel ? `Subscribe · ${priceLabel}/year` : "Subscribe with Google Play"}
      </Button>
      <Button
        data-testid="play-restore-button"
        variant="outline"
        onClick={restore}
        disabled={busy}
        className="w-full h-11 rounded-xl"
      >
        Restore purchase
      </Button>
      <p className="text-center text-[11px] text-muted-foreground">
        Billed through Google Play. Manage or cancel anytime in the Play Store.
      </p>
    </div>
  );
};

export default GooglePlayPlan;
